import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NGXLogger } from 'ngx-logger';
import { DataService } from 'src/app/shared/services/data.service';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { IDestination } from 'src/app/masters/places/destination/destination.interface';
import { BehaviorSubject, of, ReplaySubject, Subject } from 'rxjs';
import { Api } from 'src/app/shared/constants/api';
import { IDestinationCriteria, IPermissionWithShow } from 'src/app/shared/interface';
import { debounceTime, distinctUntilChanged, exhaustMap, map, take, takeUntil, tap } from 'rxjs/operators';
import { IVesselVoyage, IVesselVoyageCriteria } from '../../vessel-voyage/vessel-voyage-interface';
import { APP_ROUTES } from 'src/app/shared/constants/approutes';
import { ConfirmDailogueComponent } from 'src/app/shared/components/dailogue/confirm-dailogue.component';
import { Subscription } from 'rxjs';
import { Message } from 'src/app/shared/constants/message';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { DatePipe } from '@angular/common';
import { PreviousVesselPopupComponent } from './previous-vessel-popup/previous-vessel-popup.component';
import * as _ from 'underscore';

@Component({
    selector: 'app-weekly-consol-planning-form',
    templateUrl: './weekly-consol-planning-form.component.html',
    styleUrls: ['./weekly-consol-planning-form.component.less'],
    standalone: false
})
export class WeeklyConsolPlanningFormComponent implements OnInit, OnDestroy {

  planningForm: FormGroup;
  hoveredDate: Date | null = null;
  polSearchCtrl = this.formBuilder.control('');
  filteredPol: ReplaySubject<IDestination[]> = new ReplaySubject<IDestination[]>(1);
  destinationSearchCtrl = this.formBuilder.control('');
  filteredDestinations: ReplaySubject<IDestination[]> = new ReplaySubject<IDestination[]>(1);
  destinationDD: any = [];
  onDestroy = new Subject<void>();
  searching: boolean = false;
  reloadingVesselVoyages: boolean = false;
  weeklyDetailsDataSource = new BehaviorSubject<AbstractControl[]>([]);
  weeklyColumns = ['destination', 'vesselVoyage', 'hazPlanning', 'transitTime', 'tentativeStuffingDate', 'etdPol',
    'portCutoff', 'hazCutoff', 'siCutoff', 'routingId', 'viewPreviousVessel', 'inHandCbm', 'inHandWeight', 'shortFallCbm', 'status', 'action'];
  vesselVoyages: any = [];
  vesselVoyageSearchCtrls: FormControl[] = [];
  filteredVesselVoyagesPerRow: ReplaySubject<IVesselVoyage[]>[] = [];
  subscription = new Subscription();
  destinationFilterCtrl = this.formBuilder.control('');
  vesselVoyageFilterCtrl = this.formBuilder.control('');
  stuffingDateFilterCtrl = this.formBuilder.control('');
  routingIdFilterCtrl = this.formBuilder.control('');
  statusFilterCtrl = this.formBuilder.control('');
  confirmFlags: Map<string, boolean> = new Map();
  addPermission: boolean = false;
  editPermission: boolean = false;
  viewPermission: boolean = false;
  deletePermission: boolean = false;
  noDataAvailable: boolean = false;
  destinationSortDirection: 'asc' | 'desc' | '' = '';
  etdPolSortDirection: 'asc' | 'desc' | '' = '';
  stuffingDateSortDirection: 'asc' | 'desc' | '' = '';

  constructor(private formBuilder: FormBuilder, private utilityService: UtilityService, private router: Router,
    private service: DataService, private logger: NGXLogger, private activatedRoute: ActivatedRoute,
    private dialog: MatDialog, private permissionService: PermissionService, private datePipe: DatePipe) { }

  ngOnInit(): void {
    this.initializeForm();
    this.getAllPermission();
    this.getDestination();
    this.filterPol();
    this.filterDestination();
    this.setupColumnFilters();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  setupColumnFilters() {
    this.destinationFilterCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => this.applyColumnFilters());

    this.vesselVoyageFilterCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.applyColumnFilters());

    this.stuffingDateFilterCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.applyColumnFilters());

    this.routingIdFilterCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.applyColumnFilters());

    this.statusFilterCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.applyColumnFilters());
  }

  applyColumnFilters() {
    const allRows = this.planningDetails.controls;
    const destVal = (this.destinationFilterCtrl.value || '').toLowerCase();
    const vvVal = (this.vesselVoyageFilterCtrl.value || '').toLowerCase();
    const stuffingVal = (this.stuffingDateFilterCtrl.value || '').toLowerCase();
    const routingVal = (this.routingIdFilterCtrl.value || '').toLowerCase();
    const statusVal = (this.statusFilterCtrl.value || '').toLowerCase();

    const filtered = allRows.filter(ctrl => {
      const destName = this.destinationDD?.find(d => d.id === ctrl.get('destinationId')?.value)?.name?.toLowerCase() || '';
      const vvName = this.vesselVoyages?.find(vv => vv.id === ctrl.get('vesselVoyageId')?.value)?.vesselName?.toLowerCase() || '';
      const stuffingDate = (ctrl.get('tentativeStuffingDate')?.value || '').toString().toLowerCase();
      const routingIdValue = ctrl.get('routingId')?.value;
      const routingIdOptions = ctrl.get('routingIdOptions')?.value || [];
      const routingIdOption = routingIdOptions.find(opt => opt.id === routingIdValue);
      const routingId = (routingIdOption?.name || routingIdValue || '').toString().toLowerCase();
      const status = (ctrl.get('status')?.value || '').toString().toLowerCase();

      return (
        destName.includes(destVal) &&
        vvName.includes(vvVal) &&
        stuffingDate.includes(stuffingVal) &&
        routingId.includes(routingVal) &&
        status.includes(statusVal)
      );
    });

    if (this.destinationSortDirection) {
      filtered.sort((a, b) => {
        const nameA = this.destinationDD?.find((d: any) => d.id === a.get('destinationId')?.value)?.name || '';
        const nameB = this.destinationDD?.find((d: any) => d.id === b.get('destinationId')?.value)?.name || '';
        const nameCmp = this.destinationSortDirection === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
        if (nameCmp !== 0) return nameCmp;
        const dateA = a.get('tentativeStuffingDate')?.value ? new Date(a.get('tentativeStuffingDate').value).getTime() : 0;
        const dateB = b.get('tentativeStuffingDate')?.value ? new Date(b.get('tentativeStuffingDate').value).getTime() : 0;
        return dateA - dateB;
      });
    }

    if (this.etdPolSortDirection) {
      filtered.sort((a, b) => {
        const dateA = a.get('etdPol')?.value ? new Date(a.get('etdPol').value).getTime() : 0;
        const dateB = b.get('etdPol')?.value ? new Date(b.get('etdPol').value).getTime() : 0;
        return this.etdPolSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }

    if (this.stuffingDateSortDirection) {
      filtered.sort((a, b) => {
        const dateA = a.get('tentativeStuffingDate')?.value ? new Date(a.get('tentativeStuffingDate').value).getTime() : 0;
        const dateB = b.get('tentativeStuffingDate')?.value ? new Date(b.get('tentativeStuffingDate').value).getTime() : 0;
        return this.stuffingDateSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }

    this.weeklyDetailsDataSource.next(filtered);
  }

  sortByDestination(): void {
    this.etdPolSortDirection = '';
    this.stuffingDateSortDirection = '';
    if (this.destinationSortDirection === '') this.destinationSortDirection = 'asc';
    else if (this.destinationSortDirection === 'asc') this.destinationSortDirection = 'desc';
    else this.destinationSortDirection = '';
    this.applyColumnFilters();
  }

  sortByEtdPol(): void {
    this.destinationSortDirection = '';
    this.stuffingDateSortDirection = '';
    if (this.etdPolSortDirection === '') this.etdPolSortDirection = 'asc';
    else if (this.etdPolSortDirection === 'asc') this.etdPolSortDirection = 'desc';
    else this.etdPolSortDirection = '';
    this.applyColumnFilters();
  }

  sortByStuffingDate(): void {
    this.destinationSortDirection = '';
    this.etdPolSortDirection = '';
    if (this.stuffingDateSortDirection === '') this.stuffingDateSortDirection = 'asc';
    else if (this.stuffingDateSortDirection === 'asc') this.stuffingDateSortDirection = 'desc';
    else this.stuffingDateSortDirection = '';
    this.applyColumnFilters();
  }

  private initializeForm() {
    this.planningForm = this.formBuilder.group({
      id: [],
      weekNo: [null, [Validators.required]],
      yearNo: [null, [Validators.required]],
      fromDate: [null, [Validators.required]],
      toDate: [null, [Validators.required]],
      polId: [null, [Validators.required]],
      planningDetails: this.formBuilder.array([])
    });
    // this.planningDetails.push(this.planningDetailsForm(0));
    this.addVesselVoyageRowCtrl();
    this.weeklyDetailsDataSource.next(this.planningDetails.controls);
  }

  private planningDetailsForm(index: number, data?: any): FormGroup {
    return this.formBuilder.group({
      destinationId: [data?.destinationId || null, Validators.required],
      vesselVoyageId: [data?.vesselVoyageId || null, Validators.required],
      isHaz: [data?.isHaz ?? false],
      transitTime: [data?.transitTime || null, [Validators.required, Validators.pattern(/^\d+$/)]],
      tentativeStuffingDate: [data?.tentativeStuffingDate || null],
      etdPol: [data?.etdPol || null],
      portCutoff: [data?.portCutoff || null],
      hazCutoff: [data?.hazCutoff || null],
      siCutoff: [data?.siCutoff || null],
      routingId: [data?.routingId != null ? Number(data.routingId) : 0],
      routingIdOptions: [[]],
      inHandCbm: [data?.inHandCbm || null, Validators.pattern(/^\d+(\.\d{0,3})?$/)],
      inHandWeight: [data?.inHandWeight || null, Validators.pattern(/^\d+(\.\d{0,3})?$/)],
      shortFallCbm: [data?.shortFallCbm || null, Validators.pattern(/^\d+(\.\d{0,3})?$/)],
      status: [data?.status?.toUpperCase() || 'OPEN'],
      sequenceNo: [index + 1]
    });
  }

  onDateSelected(date: Date | null) {
    if (!date) { return; }

    const monday = this.getWeekStart(date);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const weekNo = this.getISOWeekNumber(monday);
    const yearNo = this.getISOWeekYear(monday);

    this.planningForm.patchValue({
      weekNo,
      yearNo,
      fromDate: monday,
      toDate: sunday
    });

    const polId = this.planningForm.get('polId')?.value;
    if (polId !== null && polId !== undefined && polId !== '') {
      this.checkExistingPlanning(polId);
    }
  }

  private toISODateOnly(d: Date): string {
    return new Date(
      Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
    ).toISOString().slice(0, 10);
  }

  private getWeekStart(d: Date): Date {
    const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const day = date.getDay();
    const diff = (day === 0) ? -6 : (1 - day);
    date.setDate(date.getDate() + diff);
    return date;
  }

  private getISOWeekNumber(d: Date): number {
    const date = new Date(d.getTime());
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
    const week1 = new Date(date.getFullYear(), 0, 4);
    const weekNo = 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
    return weekNo;
  }

  private getISOWeekYear(d: Date): number {
    const date = new Date(d.getTime());
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
    return date.getFullYear();
  }


  highlightWeek = (date: Date) => {
    const refDate =
      this.hoveredDate ||
      this.planningForm.get('fromDate')?.value ||
      new Date();

    if (!refDate) return '';

    const monday = this.getWeekStart(refDate);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return (date >= monday && date <= sunday) ? 'week-highlight' : '';
  }

  getDestination() {
    const criteria: IDestinationCriteria = { ids: [this.polId?.value].filter(id => id != null) };
    this.service.get(Api.DESTINATION_NAMES_FOR_DD, criteria).subscribe(res => {
      this.logger.info("Destinations: ", res)
      if (res) {
        this.destinationDD = res.contents;
        this.filteredPol.next(this.destinationDD);
        this.filteredDestinations.next(this.destinationDD);
      }
    })
  }

  filterPol() {
    this.polSearchCtrl.valueChanges.pipe(tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      exhaustMap(value => {
        if (!value) {
          this.filteredPol.next([...this.destinationDD]);
          return of([...this.destinationDD]);
        }
        return this.service.get(Api.DESTINATION_NAMES_FOR_DD, { name: value }).pipe(
          map(res => res.contents || []),
          tap(filteredResults => {
            this.filteredPol.next([...filteredResults]);
          })
        );
      })
    ).subscribe(() => this.searching = false);
  }

  filterDestination() {
    this.destinationSearchCtrl.valueChanges.pipe(
      tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      exhaustMap(value => {
        if (!value) {
          this.filteredDestinations.next([...this.destinationDD]);
          return of([...this.destinationDD]);
        }
        return this.service.get(Api.DESTINATION_NAMES_FOR_DD, { name: value }).pipe(
          map(res => res.contents || []),
          tap(filteredResults => {
            // Ensure selected destinations are retained
            const selectedIds = this.planningDetails.controls
              .map(c => c.get('destinationId')?.value)
              .filter(id => id != null);

            const merged = [
              ...filteredResults,
              ...this.destinationDD.filter(d => selectedIds.includes(d.id) && !filteredResults.some(f => f.id === d.id))
            ];

            this.filteredDestinations.next(merged);
            this.logger.info('Filtered Destinations', this.filterDestination);
          })
        );
      })
    ).subscribe(() => this.searching = false);
  }


  addVesselVoyageRowCtrl(atIndex?: number): void {
    const ctrl = this.formBuilder.control('');
    const subject = new ReplaySubject<IVesselVoyage[]>(1);
    subject.next([...this.vesselVoyages]);

    const index = atIndex ?? this.vesselVoyageSearchCtrls.length;
    this.vesselVoyageSearchCtrls.splice(index, 0, ctrl);
    this.filteredVesselVoyagesPerRow.splice(index, 0, subject);

    ctrl.valueChanges.pipe(
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged()
    ).subscribe((value: string) => {
      if (!value) {
        subject.next([...this.vesselVoyages]);
        return;
      }
      const search = value.toLowerCase();
      subject.next(
        this.vesselVoyages.filter(vv =>
          vv.vesselName?.toLowerCase().includes(search) ||
          vv.voyageNo?.toLowerCase().includes(search)
        )
      );
    });
  }

  removeVesselVoyageRowCtrl(index: number): void {
    this.vesselVoyageSearchCtrls.splice(index, 1);
    this.filteredVesselVoyagesPerRow.splice(index, 1);
  }

  refreshVesselVoyageOptions(): void {
    this.filteredVesselVoyagesPerRow.forEach(subject => subject.next([...this.vesselVoyages]));
  }

  setPolValueChangeEvent(value: number) {
    this.logger.info("polid value: ", value)
    let selectedPol: any;
    this.filteredPol.pipe(take(1)).subscribe(polList => {
      selectedPol = this.destinationDD.find(pol => pol.id === value) ||
        polList.find(pol => pol.id === value);
    });
    if (selectedPol && !this.destinationDD.some(pol => pol.id === selectedPol.id)) {
      this.destinationDD.push(selectedPol);
    }
    this.logger.info("planning form", this.planningForm.value)
    this.getVesselVoyagesForETA(value);
  }

  setDestinationValueChangeEvent(value: number, index: number) {
    this.logger.info("Set Destination called", value, index,);
    let selectedDestination: any;
    this.filteredDestinations.pipe(take(1)).subscribe(list => {
      selectedDestination = this.destinationDD.find(dest => dest?.id === value) ||
        list.find(dest => dest?.id === value);
      if (selectedDestination && !this.destinationDD.some(dest => dest?.id === selectedDestination.id)) {
        this.destinationDD.push(selectedDestination);
      }
      if (!list.some(dest => dest.id === value)) {
        this.filteredDestinations.next([...(list || []), selectedDestination]);
      }
    });
    const row = this.planningDetails.at(index);
    if (row) {
      row.get('destinationId')?.setValue(value, { emitEvent: true });

      // If vessel voyage was selected before the destination, check for duplicates now
      const existingVesselVoyageId = row.get('vesselVoyageId')?.value;
      if (existingVesselVoyageId && this.isVesselVoyageUsed(existingVesselVoyageId, index, row)) {
        row.get('vesselVoyageId')?.setValue(null);
        this.utilityService.toastrWarningMessage('Vessel Voyage already used for this Destination. Please select a different Vessel Voyage.');
      }
    }
  }

  private fetchAllDestinations(ids: number[]) {
    if (!ids || ids.length === 0) return;
    const criteria = { ids: ids };
    const pageableParams = {
      page: 0,
      size: ids.length
    };

    this.service.get(Api.DESTINATION_NAMES_FOR_DD, { ...criteria, ...pageableParams }).subscribe(res => {
      if (!res?.contents?.length) {
        this.logger.error("No destinations found for IDs:", ids);
        return;
      }

      const apiDestinations = res.contents;
      apiDestinations.forEach(dest => {
        if (!this.destinationDD.some(d => d.id === dest.id)) {
          this.destinationDD.push(dest);
        }
      });

      this.filteredDestinations.pipe(take(1)).subscribe(list => {
        const listMap = new Map(list.map(x => [x.id, x]));
        apiDestinations.forEach(dest => listMap.set(dest.id, dest));
        this.filteredDestinations.next(Array.from(listMap.values()));
      });
      this.logger.info("Fetched all destinations:", apiDestinations);
      if (this.destinationSortDirection) {
        this.applyColumnFilters();
      }
    });
  }

   getVesselVoyagesForETA(polId: number) {
    const criteria = this.buildVesselVoyageCriteria();
    this.logger.info('Vessel Voyage Criteria', criteria);

    this.service.get(Api.VESSEL_VOYAGE_FOR_DD, criteria).subscribe(res => {
      if (res) {
        this.vesselVoyages = res;
        this.logger.info("VESSEL VOYAGE DATA", res);
        this.refreshVesselVoyageOptions();
        this.checkExistingPlanning(polId);
      }
    });
  }

  private buildVesselVoyageCriteria(): IVesselVoyageCriteria {
    const now = new Date();
    const cutOffDate = new Date();
    cutOffDate.setHours(15, 0, 0, 0); // 3 PM

    const etaStartDate = new Date();
    const etaEndDate = new Date();
    etaEndDate.setDate(etaEndDate.getDate() + 90);

    if (now <= cutOffDate) {
      etaStartDate.setDate(etaStartDate.getDate() + 2);
    }
    else {
      etaStartDate.setDate(etaStartDate.getDate() + 3);
    }

    return {
      portId: this.polId.value,
      etaRanges: [
        this.datePipe.transform(etaStartDate, 'yyyy-MM-dd'),
        this.datePipe.transform(etaEndDate, 'yyyy-MM-dd')
      ]
    };
  }

  reloadVesselVoyages(): void {
    if (!this.polId.value || this.reloadingVesselVoyages) { return; }
    this.reloadingVesselVoyages = true;

    this.service.get(Api.VESSEL_VOYAGE_FOR_DD, this.buildVesselVoyageCriteria()).subscribe(res => {
      this.reloadingVesselVoyages = false;
      if (res) {
        this.vesselVoyages = res;
        this.refreshVesselVoyageOptions();
        const selectedIds = [...new Set(this.planningDetails.controls
          .map(ctrl => ctrl.get('vesselVoyageId')?.value)
          .filter(id => id))];
        this.loadVesselVoyages(selectedIds).then(() =>
          this.utilityService.notifier('Vessel voyage list refreshed', 'success'));
      }
    }, () => this.reloadingVesselVoyages = false);
  }

  setVesselVoyageValueChangeEvent(vesselVoyageId: number, rowIndex?: number, skipRoutingIdFetch = false): number[] {
    if (!this.vesselVoyages) return [];

    const selectedVoyage = this.vesselVoyages.find(vv => vv.id === vesselVoyageId);
    if (!selectedVoyage) return [];

    const polId = this.polId.value;
    const portDetail = selectedVoyage.vesselVoyagePortDetails.find(p => p.portId === polId);

    if (!portDetail) return [];

    const rowForm = this.planningDetails.at(rowIndex) as FormGroup;
    if (!rowForm) return [];

    rowForm.patchValue({
      vesselVoyageId: vesselVoyageId,
      tentativeStuffingDate: portDetail.stuffing,
      etdPol: portDetail.etd,
      portCutoff: portDetail.port,
      hazCutoff: portDetail.haz,
      siCutoff: portDetail.si
    });
    const portIds = selectedVoyage.vesselVoyagePortDetails.map(p => p.portId).filter(id => id);
    if (!skipRoutingIdFetch) {
      this.getDestinationsByPortIds(portIds, rowIndex);
    }
    return portIds;
  }

  private batchLoadRoutingIdOptions(rowPortIdsMap: Map<number, number[]>): void {
    if (!rowPortIdsMap.size) return;

    const allPortIds = [...new Set(([] as number[]).concat(...Array.from(rowPortIdsMap.values())))];
    if (!allPortIds.length) return;

    const criteria: IDestinationCriteria = { ids: allPortIds };
    this.service.get(Api.DESTINATION_NAMES_FOR_DD, criteria).subscribe(res => {
      if (!res?.contents) return;

      const destMap = new Map<number, any>(res.contents.map((p: any) => [p.id, p]));

      rowPortIdsMap.forEach((portIds, rowIndex) => {
        const routingIdOptions = [
          { id: 0, name: 'DIRECT', code: 'N/A' },
          { id: -1, name: 'TBA', code: 'N/A' },
          ...portIds.filter(id => destMap.has(id)).map(id => ({ ...destMap.get(id), id }))
        ];

        const rowForm = this.planningDetails.at(rowIndex) as FormGroup;
        if (rowForm) {
          rowForm.get('routingIdOptions')?.setValue(routingIdOptions);
          const savedRoutingId = Number(rowForm.get('routingId')?.value);
          if (routingIdOptions.some(opt => opt.id === savedRoutingId)) {
            rowForm.patchValue({ routingId: savedRoutingId }, { emitEvent: false });
          } else {
            rowForm.patchValue({ routingId: 0 }, { emitEvent: false });
          }
        }
      });
    });
  }

  loadVesselVoyages(voyageIds: number[]): Promise<void> {
    return new Promise((resolve) => {
      if (!voyageIds || voyageIds.length === 0) {
        resolve();
        return;
      }

      const missingIds = voyageIds.filter(id =>
        !this.vesselVoyages?.some(v => v.id === id)
      );

      if (missingIds.length === 0) {
        this.logger.info('loadVesselVoyages: all requested voyages already cached', voyageIds);
        resolve();
        return;
      }

      const criteria = { ids: [...new Set(missingIds)] };
      this.logger.info('loadVesselVoyages: fetching missing voyage(s)', criteria);

      this.service.get(Api.VESSEL_VOYAGES_BY_IDS, criteria).subscribe({
        next: (voyageDetails: any[]) => {
          if (Array.isArray(voyageDetails)) {
            this.logger.info(`loadVesselVoyages: received ${voyageDetails.length} voyage(s)`, voyageDetails);
            this.vesselVoyages = [...(this.vesselVoyages || []), ...voyageDetails];
            this.refreshVesselVoyageOptions();
          }
          resolve();
        },
        error: err => {
          this.logger.error('loadVesselVoyages: failed to fetch voyages', err);
          resolve();
        }
      });
    });
  }

  getDestinationsByPortIds(portIds: number[], rowIndex: number) {
    if (!portIds?.length) return;

    const criteria: IDestinationCriteria = { ids: portIds };
    this.service.get(Api.DESTINATION_NAMES_FOR_DD, criteria).subscribe(res => {
      if (res && res.contents) {
        const routingIdOptions = [
          { id: 0, name: 'DIRECT', code: 'N/A' },
          { id: -1, name: 'TBA', code: 'N/A' },
          ...res.contents.map((p: any) => ({
            ...p,
            id: Number(p.id)
          }))
        ];

        const rowForm = this.planningDetails.at(rowIndex) as FormGroup;
        if (rowForm) {
          rowForm.get('routingIdOptions')?.setValue(routingIdOptions);
          const savedRoutingId = Number(rowForm.get('routingId')?.value);
          if (routingIdOptions.some(opt => opt.id === savedRoutingId)) {
            rowForm.patchValue(
              { routingId: savedRoutingId },
              { emitEvent: false }
            );
          } else {
            rowForm.patchValue({ routingId: 0 }, { emitEvent: false });
          }
        }
      }
    });
  }

  checkExistingPlanning(polId: number) {
    const weekNo = this.planningForm.get('weekNo')?.value;
    const yearNo = this.planningForm.get('yearNo')?.value;
    const isDeleted = false;

    if (!weekNo || !yearNo || !polId) return;

    const criteria = { weekNo, yearNo, polId, isDeleted };

    this.service.get(Api.WEEKLY_CONSOL_PLANNING + '/getByCriteria', criteria).subscribe({
      next: async (res: any) => {
        if (res) {
          this.logger.info('Existing Weekly planning present for', res);
          // Does not need patching Since these values always changed only by user
          this.planningForm.patchValue({
            id: res.id,
            // weekNo: res.weekNo,
            yearNo: res.yearNo,
            // fromDate: res.fromDate,
            // toDate: res.toDate,
            // polId: res.polId
          });
          this.planningDetails.clear();
          this.vesselVoyageSearchCtrls = [];
          this.filteredVesselVoyagesPerRow = [];
          this.confirmFlags.clear();

          const voyageIds = res.planningDetails
            .map((d: any) => d.vesselVoyageId)
            .filter((id: any) => id != null);

          // Load missing voyages before patching data
          await this.loadVesselVoyages(voyageIds);

          const rowPortIdsMap = new Map<number, number[]>();
          res.planningDetails?.forEach((detail: any, idx: number) => {
            this.planningDetails.push(this.planningDetailsForm(idx, detail));
            this.addVesselVoyageRowCtrl();
            if (detail.vesselVoyageId) {
              const portIds = this.setVesselVoyageValueChangeEvent(detail.vesselVoyageId, idx, true);
              if (portIds.length) rowPortIdsMap.set(idx, portIds);
            }
          });
          this.batchLoadRoutingIdOptions(rowPortIdsMap);
          if (res.planningDetails?.length) {

            const destinationIds: number[] = res.planningDetails
              .map(d => d.destinationId)
              .filter(id => id != null);

            const uniqueIds = [...new Set(destinationIds)];
            // Fetch all destinations in one go
            this.fetchAllDestinations(uniqueIds);
          }
          this.destinationSortDirection = 'asc';
          this.etdPolSortDirection = '';
          this.stuffingDateSortDirection = '';
          this.applyColumnFilters();
          this.noDataAvailable = false;
          this.planningForm.markAsPristine();
          this.logger.info('Existing plan loaded for criteria:', criteria, res);
        } else {
          this.planningForm.patchValue({ id: null });
          this.planningDetails.clear();
          this.vesselVoyageSearchCtrls = [];
          this.filteredVesselVoyagesPerRow = [];
          this.addVesselVoyageRowCtrl();
          this.weeklyDetailsDataSource.next([...this.planningDetails.controls]);
          this.planningForm.markAsPristine();
          this.logger.info('No plan exists, starting fresh:', criteria);
          this.noDataAvailable = true;
        }
      }
    });
  }

  onSave() {
    this.planningForm.markAllAsTouched();
    if (this.planningForm.valid) {
      if (this.id.value) {
        this.openDialog(Message.SAVE_OPEN_DAILOG_IN_EDIT);
      }
      else {
        this.openDialog(Message.SAVE_OPEN_DAILOG);
      }
    } else {
      this.utilityService.toastrErrorMessage("Please fill in all mandatory fields before saving.");
    }
  }

  onCancel() {
    if (!this.planningForm.dirty) {
      // this.utilityService.toastrInfoMessage("No changes to cancel.");
      this.planningDetails.clear();
      this.weeklyDetailsDataSource.next([]);
      this.planningForm.reset();
      this.planningForm.markAsPristine();
      return;
    }
    this.openDialog(Message.CANCEL_OPEN_DAILOG_IN_EDIT, false, true);
  }

  onRevert() {
    // if (this.planningForm.dirty) {
      this.openDialog(Message.REVERT_OPEN_DAILOG, true);
    // } else {
    //   this.utilityService.toastrInfoMessage("No changes to revert.");
    // }
  }

  openDialog(message: string, revertProcess?: boolean, cancelProcess?: boolean) {
    const dialogRef = this.dialog.open(ConfirmDailogueComponent, {
      data: message,
      maxWidth: "300px",
      maxHeight: "150px"
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult === false) return;

      if (cancelProcess) {
        const polId = this.polId?.value;
        if (polId) {
          this.checkExistingPlanning(polId);
        }
      } else if (revertProcess) {
        this.revertProcess();
      } else {
        this.saveWeeklyConsolPlanning();
      }
    });
  }

  saveWeeklyConsolPlanning() {
    const isUpdate = !!this.planningForm.get('id')?.value;
    const method: 'post' | 'put' = isUpdate ? 'put' : 'post';
    const message = isUpdate ? 'updated' : 'saved';
    const formValue = this.planningForm.value;
    const payload = {
      ...formValue,
      fromDate: this.toISODateOnly(formValue.fromDate),
      toDate: this.toISODateOnly(formValue.toDate)
    };
    this.logger.info('Checking the form values and method', method, this.planningForm.value);
    this.service[method](Api.WEEKLY_CONSOL_PLANNING, payload).subscribe(res => {
      if (res) {
        this.logger.info('Saved Weekly Consol Planning:', res);
        this.id.setValue(res.id);
        this.utilityService.notifier(`Weekly Consol Planning ${message} successfully`, 'success');
        this.router.navigate([APP_ROUTES.WEEKLY_CONSOL_PLANNING]);
        // this.planningForm.markAsPristine();
      }
    });
  }

  revertProcess() {
    const currentId = this.id?.value;
    if (!currentId) return;

    this.logger.info("Reverting Weekly Consol Planning with id:", currentId);

    this.service.delete(`${Api.WEEKLY_CONSOL_PLANNING}/${currentId}`).subscribe({
      next: () => {
        this.logger.info("Successfully reverted record:", currentId);
        this.utilityService.toastrSuccessMessage("Weekly Consol Planning reverted successfully");
        this.resetForm();
      }
    });
  }

  resetForm() {
    this.planningForm.reset();
    this.planningDetails.clear();
    this.vesselVoyageSearchCtrls = [];
    this.filteredVesselVoyagesPerRow = [];
    this.confirmFlags.clear();
    // this.planningDetails.push(this.planningDetailsForm(0));
    this.addVesselVoyageRowCtrl();
    this.weeklyDetailsDataSource.next([...this.planningDetails.controls]);
  }

  addRow() {
    if (this.planningDetails.length === 0) {
      this.planningDetails.push(this.planningDetailsForm(0));
      this.addVesselVoyageRowCtrl(0);
      this.applyColumnFilters();
      this.noDataAvailable = false;
      this.planningForm.markAsDirty();
      return;
    }
    const firstRow = this.planningDetails.at(0);
    if (firstRow.valid) {
      this.planningDetails.insert(0, this.planningDetailsForm(0));
      this.addVesselVoyageRowCtrl(0);
      this.planningDetails.controls.forEach((control, i) => {
        (control as FormGroup).patchValue({ sequenceNo: i + 1 }, { emitEvent: false });
      });
      this.applyColumnFilters();
      this.planningForm.markAsDirty();
      this.logger.info("planning form: ", this.planningForm.value);
    } else {
      this.utilityService.toastrErrorMessage("Please fill in all mandatory fields before adding a new row.")
      return;
    }
  }

  deleteRow(index: number) {
    if (this.planningDetails.length > 1) {
      const dialogRef = this.dialog.open(ConfirmDailogueComponent, {
        data: Message.DELETE_OPEN_DAILOG,
        maxWidth: "300px",
        maxHeight: "150px"
      });

      dialogRef.afterClosed().subscribe(confirmed => {
        if (!confirmed) return;
        this.planningDetails.removeAt(index);
        this.removeVesselVoyageRowCtrl(index);
        this.planningDetails.controls.forEach((control, i) => {
          control.patchValue({ sequenceNo: i + 1 });
        });
        this.applyColumnFilters();
        this.planningForm.markAsDirty();
      });
    }
  }

  openPreviousVesselPopup(element: any) {
    this.logger.info("The values of the Element", element);
    this.dialog.open(PreviousVesselPopupComponent, {
      width: '1100px',
      height: '500px',
      data: {
        weekNo: this.planningForm.get('weekNo')?.value,
        yearNo: this.planningForm.get('yearNo')?.value,
        fromDate: this.toISODateOnly(this.planningForm.get('fromDate')?.value),
        toDate: this.toISODateOnly(this.planningForm.get('toDate')?.value),
        polId: this.planningForm.get('polId')?.value,
        isDeleted: false,
        destinationId: element?.destinationId,
        destinationName: this.destinationDD?.find(x => x.id == element?.destinationId)?.name,
        vesselVoyageId: element?.vesselVoyageId,
        transitTime: element?.transitTime,
        isHazL: element?.isHaz,
      }
    });
  }

  copyFromPreviousWeek() {
    const weekNo = this.planningForm.get('weekNo')?.value;
    const yearNo = this.planningForm.get('yearNo')?.value;
    const polId = this.planningForm.get('polId')?.value;

    if (!weekNo || !yearNo || !polId) {
      this.logger.info('copyFromPreviousWeek: skipped — missing weekNo, yearNo or polId');
      return;
    }

    const criteria = { weekNo, yearNo, polId, isDeleted: false };
    this.logger.info('copyFromPreviousWeek: fetching previous week data', criteria);

    this.service.post(Api.COPY_PREVIOUS_WEEK_DATA, criteria).subscribe(res => {
      if (!res?.planningDetails?.length) {
        this.logger.info('copyFromPreviousWeek: no planning details found for previous week');
        this.utilityService.toastrWarningMessage("No planning details available for the previous week.");
        return;
      }

      this.logger.info(`copyFromPreviousWeek: received ${res.planningDetails.length} detail(s)`, res);

      const dialogRef = this.dialog.open(ConfirmDailogueComponent, {
        data: "Do you want to add previous week destinations to this week?",
        maxWidth: "300px",
        maxHeight: "150px"
      });

      dialogRef.afterClosed().subscribe(async confirmed => {
        if (!confirmed) return;
        await this.applyPreviousWeekDetails(res.planningDetails);
      });
    });
  }

  private async applyPreviousWeekDetails(planningDetails: any[]): Promise<void> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Step 1: Deduplicate by destination
    const uniqueDetails: any[] = planningDetails.filter(
      (detail, index, self) => index === self.findIndex(d => d.destinationId === detail.destinationId)
    );
    this.logger.info(`applyPreviousWeekDetails: ${uniqueDetails.length} unique destination(s) to apply`);

    // Step 2: Pre-fetch all vessel voyages — we need the port details to check stuffing dates
    const voyageIds = [...new Set<number>(
      uniqueDetails.map(d => d.vesselVoyageId).filter(id => id != null)
    )];
    this.logger.info(`applyPreviousWeekDetails: pre-loading ${voyageIds.length} vessel voyage(s)`, voyageIds);
    await this.loadVesselVoyages(voyageIds);

    // Derive the stuffing date from the fetched voyage's port detail matching current POL
    const polId = this.polId.value;
    const shouldCopyVessel = (detail: any): boolean => {
      if (!detail.vesselVoyageId) return false;
      const voyage = this.vesselVoyages.find((vv: any) => vv.id === detail.vesselVoyageId);
      if (!voyage) return false;
      const portDetail = voyage.vesselVoyagePortDetails?.find((p: any) => p.portId === polId);
      if (!portDetail?.stuffing) return false;
      const stuffingDate = new Date(portDetail.stuffing);
      stuffingDate.setHours(0, 0, 0, 0);
      return stuffingDate >= today;
    };

    // Step 3: Fill the existing blank first row in-place, then append the rest
    //         Collect portIds per row for a single batched routingId fetch (Step 4)
    const rowPortIdsMap = new Map<number, number[]>();
    const firstRow = this.planningDetails.at(0) as FormGroup;
    const isFirstRowBlank = !!firstRow && !firstRow.get('destinationId')?.value;
    let startIndex = 0;

    if (isFirstRowBlank && uniqueDetails.length > 0) {
      const firstDetail = uniqueDetails[0];
      this.logger.info('applyPreviousWeekDetails: filling blank first row — destinationId:', firstDetail.destinationId);
      firstRow.patchValue({ destinationId: firstDetail.destinationId });
      if (shouldCopyVessel(firstDetail)) {
        this.logger.info(`applyPreviousWeekDetails: row 0 — copying vesselVoyageId ${firstDetail.vesselVoyageId} (stuffing >= today)`);
        const portIds = this.setVesselVoyageValueChangeEvent(firstDetail.vesselVoyageId, 0, true);
        if (portIds.length) rowPortIdsMap.set(0, portIds);
      } else if (firstDetail.vesselVoyageId) {
        this.logger.info(`applyPreviousWeekDetails: row 0 — skipping vesselVoyageId ${firstDetail.vesselVoyageId} (stuffing already passed)`);
      }
      startIndex = 1;
    }

    const currentLength = this.planningDetails.length;
    const detailsToAppend = uniqueDetails.slice(startIndex);
    this.logger.info(`applyPreviousWeekDetails: appending ${detailsToAppend.length} new row(s) from index ${currentLength}`);

    detailsToAppend.forEach((detail, index) => {
      const rowIndex = currentLength + index;
      const row = this.planningDetailsForm(rowIndex);
      row.patchValue({ destinationId: detail.destinationId });
      this.planningDetails.push(row);
      this.addVesselVoyageRowCtrl();
      if (shouldCopyVessel(detail)) {
        this.logger.info(`applyPreviousWeekDetails: row ${rowIndex} — copying vesselVoyageId ${detail.vesselVoyageId} (stuffing >= today)`);
        const portIds = this.setVesselVoyageValueChangeEvent(detail.vesselVoyageId, rowIndex, true);
        if (portIds.length) rowPortIdsMap.set(rowIndex, portIds);
      } else if (detail.vesselVoyageId) {
        this.logger.info(`applyPreviousWeekDetails: row ${rowIndex} — skipping vesselVoyageId ${detail.vesselVoyageId} (stuffing already passed)`);
      }
    });

    // Step 4: Batch-fetch routingId options for all rows that have a vessel — one API call total
    if (rowPortIdsMap.size) {
      this.logger.info(`applyPreviousWeekDetails: batch-loading routingId options for ${rowPortIdsMap.size} row(s)`);
      this.batchLoadRoutingIdOptions(rowPortIdsMap);
    }

    // Step 5: Re-sequence all rows
    this.planningDetails.controls.forEach((control, i) => {
      control.patchValue({ sequenceNo: i + 1 }, { emitEvent: false });
    });

    // Step 6: Refresh the table datasource
    this.weeklyDetailsDataSource.next([...this.planningDetails.controls]);

    // Step 7: Fetch any destination names missing from the dropdown cache
    const missingDestinationIds = [...new Set<number>(
      uniqueDetails
        .map(d => d.destinationId)
        .filter(id => id != null && !this.destinationDD.some((dd: any) => dd.id === id))
    )];
    if (missingDestinationIds.length) {
      this.logger.info(`applyPreviousWeekDetails: fetching ${missingDestinationIds.length} missing destination name(s)`, missingDestinationIds);
      this.fetchAllDestinations(missingDestinationIds);
    }
    this.noDataAvailable = false;
    this.planningForm.markAsDirty();
    this.logger.info('applyPreviousWeekDetails: completed successfully');
    this.utilityService.toastrSuccessMessage("Previous week data added successfully");
  }

  /**
   * Returns the true index of a row's AbstractControl in the planningDetails FormArray.
   * This is needed because the table uses a filtered datasource,
   * so the template's `let i = index` reflects the filtered position, not the real FormArray index.
   */
  getRowIndex(element: AbstractControl): number {
    return this.planningDetails.controls.indexOf(element);
  }

  onDateFieldChange(element: AbstractControl, field: string): void {
    const key = `${this.getRowIndex(element)}_${field}`;
    this.confirmFlags.set(key, true);
  }

  getConfirmFlag(field: string, rowIndex: number): boolean {
    return this.confirmFlags.get(`${rowIndex}_${field}`) === true;
  }

  onConfirm(element: FormGroup, controlName: string) {
    const key = `${this.getRowIndex(element)}_${controlName}`;
    const body = this.prepareBodyForDate(element, controlName);
    this.logger.info("Body to update the Vessel Voyage dates", body);
    this.service.put(`${Api.VESSEL_VOYAGE_UPDATE_DATE}`, body).subscribe(res => {
      if (res) {
        this.confirmFlags.delete(key);
        const updatedVesselVoyageId = element.get('vesselVoyageId')?.value;
        const newDate = element.get(controlName)?.value;

        // Sync the in-memory cache so new rows selecting the same voyage get the updated date
        const portDetailKey = controlName === 'tentativeStuffingDate' ? 'stuffing' : 'etd';
        const cachedVoyage = this.vesselVoyages?.find((vv: any) => vv.id === updatedVesselVoyageId);
        if (cachedVoyage) {
          const portDetail = cachedVoyage.vesselVoyagePortDetails?.find((p: any) => p.portId === this.polId.value);
          if (portDetail) portDetail[portDetailKey] = newDate;
        }

        this.planningDetails.controls.forEach((ctrl, idx) => {
          if (ctrl !== element && ctrl.get('vesselVoyageId')?.value === updatedVesselVoyageId) {
            ctrl.get(controlName)?.setValue(newDate, { emitEvent: false });
            this.confirmFlags.delete(`${idx}_${controlName}`);
          }
        });
        this.utilityService.toastrSuccessMessage('Date updated successfully');
      }
    });
  }

  prepareBodyForDate(element: FormGroup, controlName: string) {
    const rawDate = element.get(controlName)?.value;
    return {
      vesselVoyageId: element.get('vesselVoyageId')?.value,
      polId: this.planningForm.get('polId')?.value,
      [controlName]: rawDate ? this.toISODateOnly(rawDate) : null
    };
  }

  isVesselVoyageUsed(vesselVoyageId: number, currentRowIndex: number, currentRowControl: AbstractControl): boolean {
    const currentDestinationId = currentRowControl.get('destinationId')?.value;

    // If no destination is selected yet in this row, nothing to block
    if (!currentDestinationId || !vesselVoyageId) return false;

    // Check all other rows for the same Destination + Vessel Voyage combination
    const otherRows = this.planningDetails.controls.filter((_, idx) => idx !== currentRowIndex);

    return otherRows.some(row => {
      const isSameDestination = row.get('destinationId')?.value === currentDestinationId;
      const isSameVesselVoyage = row.get('vesselVoyageId')?.value === vesselVoyageId;
      return isSameDestination && isSameVesselVoyage;
    });
  }

  getAllPermission() {
    let permissionCodes = [PermissionKey.WEEKLY_CONSOL_PLANNING_ADD, PermissionKey.WEEKLY_CONSOL_PLANNING_DELETE,
    PermissionKey.WEEKLY_CONSOL_PLANNING_EDIT, PermissionKey.WEEKLY_CONSOL_PLANNING_VIEW];
    let permissionsWithShowValue: IPermissionWithShow[] = this.permissionService.checkPermissions(permissionCodes, PermissionKey.WEEKLY_CONSOL_PLANNING);
    this.addPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.WEEKLY_CONSOL_PLANNING_ADD })?.show ? true : false;
    this.editPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.WEEKLY_CONSOL_PLANNING_EDIT })?.show ? true : false;
    this.deletePermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.WEEKLY_CONSOL_PLANNING_DELETE })?.show ? true : false;
    this.viewPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.WEEKLY_CONSOL_PLANNING_VIEW })?.show ? true : false;
  }

  get polId() { return this.planningForm.get('polId'); }
  get planningDetails(): FormArray { return this.planningForm.get('planningDetails') as FormArray; }
  get id() { return this.planningForm.get('id'); }

  restrictDecimalInput(event: KeyboardEvent, decimalPlaces: number) {
    const input = event.target as HTMLInputElement;
    const { key } = event;

    if (['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(key)) return;

    if (!/[\d.]/.test(key) || (key === '.' && input.value.includes('.'))) {
      event.preventDefault();
      return;
    }

    const cursorPosition = input.selectionStart || 0;
    const [integerPart = '', decimalPart = ''] = input.value.split('.');
    const decimalStart = integerPart.length;

    if (
      input.value.includes('.') &&
      cursorPosition > decimalStart &&
      decimalPart.length >= decimalPlaces &&
      key !== 'Backspace'
    ) {
      event.preventDefault();
    }
  }
}
